import { ZapCompactIcon } from '@vizit/icons';
import { cx } from '@vizit/theme';
import * as React from 'react';
import { Button } from 'react-aria-components';
import { Text, TextColor, TextVariant } from '../text';
import { Tooltip, TooltipTrigger } from '../tooltip';
import { type WeightIndicatorProps, WeightIndicatorSize } from './types';

/**
 * Single bolt + exact weight percentage ("⚡ 33%") shown in a metric cell header for
 * formula-scored PDPs. The GAM-era 1–4 bolt display remains `ImpactBolt`; the two coexist.
 *
 * @example
 * <WeightIndicator pct={33} />
 * <WeightIndicator pct={33} accentClassName="text-pink-600" />
 */
const WeightIndicator = React.forwardRef<HTMLButtonElement, WeightIndicatorProps>((props, forwardedRef) => {
  const { pct, accentClassName, isDisabled = false, size = WeightIndicatorSize.Md, className } = props;

  const hasAccent = !isDisabled && accentClassName != null;
  const isLarge = size === WeightIndicatorSize.Lg;

  return (
    <TooltipTrigger delay={200}>
      <Button
        ref={forwardedRef}
        aria-label={`Weight: ${pct}% of product page score`}
        className={cx(
          'group/weight inline-flex items-center gap-2 cursor-pointer transition-all duration-150',
          isDisabled && 'text-fg-disabled-subtle',
          className,
        )}
        data-testid="weight-indicator"
        data-accent={hasAccent ? 'true' : 'false'}
        data-size={size}
      >
        {/* The accent tints the bolt only; the percentage settles to tertiary so the colour reads as a key, not a value. */}
        <ZapCompactIcon
          size={isLarge ? 18 : 14}
          color="currentColor"
          className={cx(
            'transition-colors duration-150',
            hasAccent && accentClassName,
            !hasAccent && !isDisabled && 'text-quinary group-hover/weight:text-quinary-hover',
          )}
        />
        <Text
          variant={isLarge ? TextVariant.MonoSm : TextVariant.MonoXs}
          color={isDisabled ? TextColor.Inherit : hasAccent ? TextColor.Tertiary : TextColor.Quaternary}
          showEllipsis={false}
          // No 14px mono variant exists: MonoSm carries the family and weight, text-sm the size.
          className={cx(
            'transition-colors duration-150',
            isLarge && 'text-sm',
            !isDisabled &&
              (hasAccent ? 'group-hover/weight:text-tertiary-hover' : 'group-hover/weight:text-quaternary-hover'),
          )}
        >
          {pct}%
        </Text>
      </Button>
      <Tooltip>{`This score contributes to ${pct}% of your overall product page score`}</Tooltip>
    </TooltipTrigger>
  );
});

WeightIndicator.displayName = 'WeightIndicator';

export { WeightIndicator };
export { WeightIndicatorSize } from './types';
export type { WeightIndicatorProps } from './types';
